import React, { useState } from 'react';
import { FiCreditCard, FiEdit2, FiLock } from 'react-icons/fi';
import { useStateContext } from '../../contexts/ContextProvider';
import { SecuritySettings } from '../../components';
import StaffBankDetailsModal from '../../components/Finance/StaffBankDetailsModal';

const TeacherSettings = () => {
  const { user, school } = useStateContext();
  const [activeTab, setActiveTab] = useState('security');
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankDetails, setBankDetails] = useState(user?.counter_party || null);

  const tabs = [
    { id: 'security', label: 'Security', icon: FiLock },
    { id: 'bank', label: 'Bank Account', icon: FiCreditCard },
  ];

  return (
    <div className="m-2 md:m-5 mt-24 p-4 md:p-6 lg:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-xl md:text-2xl font-bold">Settings</h1>
      </div>

      {/* Tab Bar */}
      <div className="border-b pb-3 mb-8 flex gap-6">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 text-sm font-semibold pb-3 -mb-[13px] border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-indigo-700 border-indigo-700'
                  : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Security Tab */}
      {activeTab === 'security' && (
        <SecuritySettings userId={user?._id} schoolId={school?._id} />
      )}

      {/* Bank Account Tab */}
      {activeTab === 'bank' && (
        <div className="max-w-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">My Bank Account</h2>
              <p className="text-sm text-gray-500 mt-1">
                Your bank details for receiving salary disbursements.
              </p>
            </div>
            <button
              onClick={() => setBankModalOpen(true)}
              className="flex items-center gap-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-medium text-sm px-4 py-2 rounded-full transition-colors"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              {bankDetails?.account_number ? 'Edit' : 'Set Up'}
            </button>
          </div>

          {bankDetails?.account_number ? (
            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Bank Name</p>
                  <p className="text-sm font-medium text-gray-800">{bankDetails.bank_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Account Number</p>
                  <p className="text-sm font-medium text-gray-800">{bankDetails.account_number}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Account Name</p>
                  <p className="text-sm font-medium text-gray-800">{bankDetails.account_name || '—'}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-4 py-3 text-sm">
              <FiCreditCard className="shrink-0" />
              <span>
                You haven't set up your bank account yet. Add your bank details so you can receive salary payments.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Bank Details Modal */}
      <StaffBankDetailsModal
        isOpen={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        schoolId={school?._id}
        staffMember={user ? { ...user, counter_party: bankDetails } : null}
        onSaved={(staffId, updatedCp) => {
          setBankDetails(updatedCp);
          setBankModalOpen(false);
        }}
      />
    </div>
  );
};

export default TeacherSettings;
